import type { MouseEvent } from "react"
import { Link } from "react-router-dom"
import { MarketingBrand } from "@/components/landing/MarketingBrand"

// Shared marketing footer — used on the landing page, legal pages, and any
// future public marketing pages. Anchor links use a leading "/" so they
// navigate to the landing page from any route before scrolling.

const INDUSTRY_LINKS = [
  "Building & Construction",
  "Infrastructure & Engineering",
  "Real Estate & Facilities",
  "Government Contracts",
] as const

// When the user is already on the landing page, intercept hash navigation and
// scroll smoothly. Avoids React Router's ScrollRestoration jumping to top on
// the first click. From other pages, falls through to native navigation.
function handleAnchorNav(e: MouseEvent<HTMLAnchorElement>, href: string) {
  if (!href.startsWith("/#")) return
  if (window.location.pathname !== "/") return
  e.preventDefault()
  const target = document.getElementById(href.slice(2))
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" })
    window.history.replaceState(null, "", href.slice(1))
  }
}

interface LandingFooterProps {
  variant?: "full" | "minimal"
}

export function LandingFooter({ variant = "full" }: LandingFooterProps = {}) {
  if (variant === "minimal") {
    return (
      <footer className="border-t border-[#e5e7eb] py-8" style={{ color: "#4a4a4a" }}>
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div>© {new Date().getFullYear()} BidContext. All rights reserved.</div>
          <div className="flex items-center gap-6 flex-wrap justify-center sm:justify-end">
            <Link to="/privacy" className="hover:text-[#6024EE] transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-[#6024EE] transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="hover:text-[#6024EE] transition-colors">
              Cookies Policy
            </Link>
            <a href="mailto:hello@bidcontext.ai" className="hover:text-[#6024EE] transition-colors">
              Contact
            </a>
          </div>
        </div>
      </footer>
    )
  }

  return (
    <footer
      data-section-bg="white"
      className="py-20"
      style={{ background: "var(--lp-bg)", color: "var(--lp-muted)" }}
    >
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
        {/* Brand + tagline */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <MarketingBrand />
          </div>
          <p className="text-[15px] leading-relaxed max-w-xs">
            Built to capture your unique corporate intelligence and win more work.
          </p>
        </div>

        {/* Product */}
        <div>
          <div className="text-xs uppercase tracking-wider mb-4 font-medium" style={{ color: "var(--lp-muted)", opacity: 0.65 }}>
            Product
          </div>
          <ul className="space-y-3 text-[15px]">
            <li>
              <a
                href="/#features"
                onClick={(e) => handleAnchorNav(e, "/#features")}
                className="hover:text-[#6024EE] transition-colors"
              >
                Features
              </a>
            </li>
            <li>
              <a
                href="/#how-it-works"
                onClick={(e) => handleAnchorNav(e, "/#how-it-works")}
                className="hover:text-[#6024EE] transition-colors"
              >
                How It Works
              </a>
            </li>
            <li>
              <Link to="/security" className="hover:text-[#6024EE] transition-colors">
                Security
              </Link>
            </li>
          </ul>
        </div>

        {/* Industries */}
        <div>
          <div className="text-xs uppercase tracking-wider mb-4 font-medium" style={{ color: "var(--lp-muted)", opacity: 0.65 }}>
            Industries
          </div>
          <ul className="space-y-3 text-[15px]">
            {INDUSTRY_LINKS.map((title) => (
              <li key={title}>
                <a
                  href="/#industries"
                  onClick={(e) => handleAnchorNav(e, "/#industries")}
                  className="hover:text-[#6024EE] transition-colors"
                >
                  {title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom row: copyright (left) + legal/contact links (right) */}
      <div
        className="max-w-7xl mx-auto px-6 mt-16 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm"
        style={{ borderTop: "1px solid var(--lp-border)" }}
      >
        <div>© {new Date().getFullYear()} BidContext. All rights reserved.</div>
        <div className="flex items-center gap-6 flex-wrap justify-center sm:justify-end">
          <Link to="/privacy" className="hover:text-[#6024EE] transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms" className="hover:text-[#6024EE] transition-colors">
            Terms of Service
          </Link>
          <Link to="/cookies" className="hover:text-[#6024EE] transition-colors">
            Cookies Policy
          </Link>
          <a href="mailto:hello@bidcontext.ai" className="hover:text-[#6024EE] transition-colors">
            Contact
          </a>
        </div>
      </div>
    </footer>
  )
}
